import { Info } from 'lucide-react'

import type { ArtifactResponse } from '@/client/types.gen'
import { Button as UIButton } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useTranslation } from '@/hooks/useTranslation'
import { TOUR } from '@/lib/product-tour/constants'

import { ArtifactCard } from './ArtifactCard'
import type { ArtifactSelectionProps, StepComponentProps } from './types'
import {
	canAddAnalysisSource,
	getAvailableForContext,
	getCompletedArtifacts,
	getTotalSourceCount,
	MAX_ANALYSIS_SOURCES,
} from './utils'

interface ContextFilesStepProps extends ArtifactSelectionProps, StepComponentProps {
	primaryFiles: ArtifactResponse[]
}

export function ContextFilesStep({
	artifacts,
	selectedFiles = [],
	primaryFiles = [],
	excludedFromContextIds = [],
	onToggle,
	onContinue,
}: ContextFilesStepProps) {
	const { t } = useTranslation()
	const completedArtifacts = getCompletedArtifacts(artifacts)
	const availableForContext = getAvailableForContext(completedArtifacts, excludedFromContextIds)
	const selectedSourceCount = getTotalSourceCount(primaryFiles, selectedFiles)
	const canAddMoreSources = canAddAnalysisSource(primaryFiles, selectedFiles)

	return (
		<div className="flex-1 flex flex-col min-h-0 space-y-4">
			<div>
				<div className="flex items-center gap-2 mb-1.5">
					<h2 className="text-lg font-semibold text-foreground">
						{t('pages.projectDetail.modal.step2.title')}
					</h2>
					<Tooltip>
						<TooltipTrigger asChild>
							<Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
						</TooltipTrigger>
						<TooltipContent className="max-w-sm text-left">
							<p className="text-pretty">{t('pages.projectDetail.modal.step2.tooltip')}</p>
						</TooltipContent>
					</Tooltip>
				</div>
				<p className="text-sm text-muted-foreground">
					{t('pages.projectDetail.modal.sourceLimit', {
						count: selectedSourceCount,
						max: MAX_ANALYSIS_SOURCES,
					})}
				</p>
			</div>

			<div className="flex-1 overflow-y-auto px-1 py-1">
				<div className="space-y-2.5">
					{availableForContext.length > 0 ? (
						availableForContext.map((artifact) => {
							const isSelected = selectedFiles.find((f) => f.id === artifact.id)
							return (
								<ArtifactCard
									key={artifact.id}
									artifact={artifact}
									isSelected={!!isSelected}
									mode="multi"
									disabled={!canAddMoreSources}
									onToggle={onToggle}
								/>
							)
						})
					) : (
						<div className="text-sm text-muted-foreground text-center py-6">
							{t('pages.projectDetail.modal.step2.noContextFiles')}
						</div>
					)}
				</div>
			</div>

			<div className="flex gap-2">
				<UIButton
					type="button"
					onClick={onContinue}
					className="flex-[2]"
					data-tour={TOUR.modalContextContinue}
				>
					{selectedFiles.length > 0
						? t('pages.projectDetail.modal.step2.continueWithFiles', { count: selectedFiles.length })
						: t('common.skip')}
				</UIButton>
			</div>
		</div>
	)
}
